import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

// Game categories
const categories = [
  { id: 'all', name: 'All Games', icon: 'logo-white' },
  { id: 'articulation', name: 'Articulation', icon: '👄' },
  { id: 'vocabulary', name: 'Vocabulary', icon: '📚' },
  { id: 'social', name: 'Social Skills', icon: '👋' },
  { id: 'numbers', name: 'Numbers & Counting', icon: '🔢' },
  { id: 'sounds', name: 'Sounds & Phonics', icon: '🔊' },
  { id: 'requesting', name: 'Requesting', icon: '🙋' },
];

// Age groups
const ageGroups = [
  { id: 'all', name: 'All Ages' },
  { id: '2-4', name: '2-4 years' },
  { id: '4-6', name: '4-6 years' },
  { id: '6-8', name: '6-8 years' },
  { id: '8+', name: '8+ years' },
];

// Difficulty levels
const difficulties = [
  { id: 'all', name: 'All Levels', color: 'bg-navy-100 text-navy-600' },
  { id: 'beginner', name: 'Beginner', color: 'bg-mint-100 text-mint-700' },
  { id: 'intermediate', name: 'Intermediate', color: 'bg-sunshine-100 text-sunshine-700' },
  { id: 'advanced', name: 'Advanced', color: 'bg-coral-100 text-coral-700' },
];

// Placeholder game data (will be dynamic later)
const allGames = [
  {
    id: 1,
    title: 'Balloon Pop',
    description: 'Pop colorful balloons by saying target words! Great for practicing articulation.',
    icon: '🎈',
    color: 'from-coral-400 to-coral-500',
    category: 'articulation',
    age: '2-4',
    difficulty: 'beginner',
    skills: ['Articulation', 'Volume Control'],
    plays: '12.5K',
    rating: 4.9,
    isNew: false,
    isFeatured: true,
  },
  {
    id: 2,
    title: 'Rocket Launch',
    description: 'Count down from 10 and blast off to space! Perfect for number practice.',
    icon: '🚀',
    color: 'from-navy-600 to-navy-800',
    category: 'numbers',
    age: '4-6',
    difficulty: 'beginner',
    skills: ['Numbers', 'Sequencing'],
    plays: '9.8K',
    rating: 4.8,
    isNew: false,
    isFeatured: true,
  },
  {
    id: 3,
    title: 'Fruit Slice',
    description: 'Slice fruits ninja-style by naming them! Learn fruit vocabulary.',
    icon: '🍎',
    color: 'from-mint-400 to-teal-500',
    category: 'vocabulary',
    age: '4-6',
    difficulty: 'beginner',
    skills: ['Vocabulary', 'Pronunciation'],
    plays: '15.2K',
    rating: 4.9,
    isNew: false,
    isFeatured: true,
  },
  {
    id: 4,
    title: "Pip's Kitchen",
    description: 'Help Pip cook delicious meals by requesting ingredients! Learn requesting skills.',
    icon: '🍳',
    color: 'from-sunshine-400 to-coral-400',
    category: 'requesting',
    age: '4-6',
    difficulty: 'intermediate',
    skills: ['Requesting', 'Social Skills'],
    plays: '18.3K',
    rating: 5.0,
    isNew: false,
    isFeatured: true,
  },
  {
    id: 5,
    title: 'Animal Chat',
    description: 'Have conversations with friendly animals and learn their sounds!',
    icon: '🦁',
    color: 'from-lavender-400 to-lavender-500',
    category: 'sounds',
    age: '2-4',
    difficulty: 'beginner',
    skills: ['Animal Sounds', 'Imitation'],
    plays: '11.1K',
    rating: 4.7,
    isNew: false,
    isFeatured: false,
  },
  {
    id: 6,
    title: 'Speed Race',
    description: 'Race cars around the track by speaking quickly and clearly!',
    icon: '🏎️',
    color: 'from-sky-400 to-sky-500',
    category: 'articulation',
    age: '6-8',
    difficulty: 'intermediate',
    skills: ['Fluency', 'Pacing'],
    plays: '7.4K',
    rating: 4.6,
    isNew: false,
    isFeatured: false,
  },
  {
    id: 7,
    title: 'Color Quest',
    description: 'Find hidden colors in magical scenes by calling out their names!',
    icon: '🎨',
    color: 'from-lavender-400 to-coral-400',
    category: 'vocabulary',
    age: '2-4',
    difficulty: 'beginner',
    skills: ['Colors', 'Visual Scanning'],
    plays: '8.9K',
    rating: 4.8,
    isNew: true,
    isFeatured: false,
  },
  {
    id: 8,
    title: 'Shape Builder',
    description: 'Build structures by naming shapes! Great for geometry vocabulary.',
    icon: '🔷',
    color: 'from-teal-400 to-navy-500',
    category: 'vocabulary',
    age: '4-6',
    difficulty: 'beginner',
    skills: ['Shapes', 'Spatial Awareness'],
    plays: '6.2K',
    rating: 4.5,
    isNew: true,
    isFeatured: false,
  },
  {
    id: 9,
    title: 'Weather Wizard',
    description: 'Control the weather by describing it! Sun, rain, snow - you decide!',
    icon: '🌈',
    color: 'from-sky-400 to-teal-400',
    category: 'vocabulary',
    age: '4-6',
    difficulty: 'intermediate',
    skills: ['Weather Words', 'Descriptive Language'],
    plays: '5.1K',
    rating: 4.7,
    isNew: true,
    isFeatured: false,
  },
  {
    id: 10,
    title: 'Playground Friends',
    description: 'Make friends on the playground by practicing social phrases!',
    icon: '🛝',
    color: 'from-mint-400 to-sunshine-400',
    category: 'social',
    age: '4-6',
    difficulty: 'intermediate',
    skills: ['Greetings', 'Social Phrases'],
    plays: '9.3K',
    rating: 4.9,
    isNew: false,
    isFeatured: false,
  },
  {
    id: 11,
    title: 'Story Time',
    description: 'Fill in the blanks to create silly stories! Perfect for sentence building.',
    icon: '📖',
    color: 'from-coral-400 to-lavender-400',
    category: 'vocabulary',
    age: '6-8',
    difficulty: 'intermediate',
    skills: ['Sentence Structure', 'Creativity'],
    plays: '4.8K',
    rating: 4.6,
    isNew: false,
    isFeatured: false,
  },
  {
    id: 12,
    title: 'Music Maker',
    description: 'Create music by saying instrument names and sounds!',
    icon: '🎵',
    color: 'from-lavender-400 to-sky-400',
    category: 'sounds',
    age: '4-6',
    difficulty: 'beginner',
    skills: ['Instrument Names', 'Sound Imitation'],
    plays: '7.7K',
    rating: 4.8,
    isNew: false,
    isFeatured: false,
  },
  {
    id: 13,
    title: 'Ocean Explorer',
    description: 'Dive deep and discover sea creatures by naming them!',
    icon: '🐠',
    color: 'from-teal-400 to-navy-600',
    category: 'vocabulary',
    age: '4-6',
    difficulty: 'beginner',
    skills: ['Sea Animals', 'Descriptive Language'],
    plays: '6.5K',
    rating: 4.7,
    isNew: true,
    isFeatured: false,
  },
  {
    id: 14,
    title: 'Space Station',
    description: 'Command a space station using specific action words!',
    icon: '🛸',
    color: 'from-navy-600 to-lavender-500',
    category: 'requesting',
    age: '6-8',
    difficulty: 'advanced',
    skills: ['Action Words', 'Following Directions'],
    plays: '3.9K',
    rating: 4.5,
    isNew: false,
    isFeatured: false,
  },
  {
    id: 15,
    title: 'Dino World',
    description: 'Wake up dinosaurs by roaring their names!',
    icon: '🦕',
    color: 'from-mint-400 to-teal-500',
    category: 'vocabulary',
    age: '2-4',
    difficulty: 'beginner',
    skills: ['Dinosaur Names', 'Volume Control'],
    plays: '10.2K',
    rating: 4.9,
    isNew: false,
    isFeatured: false,
  },
  {
    id: 16,
    title: 'Magic Garden',
    description: 'Grow flowers and plants by naming them correctly!',
    icon: '🌻',
    color: 'from-sunshine-400 to-mint-400',
    category: 'vocabulary',
    age: '4-6',
    difficulty: 'beginner',
    skills: ['Plant Names', 'Colors'],
    plays: '5.6K',
    rating: 4.6,
    isNew: true,
    isFeatured: false,
  },
];

export default function GamesLibrary() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedAge, setSelectedAge] = useState('all');
  const [selectedDifficulty, setSelectedDifficulty] = useState('all');
  const [sortBy, setSortBy] = useState('popular');
  const [filteredGames, setFilteredGames] = useState(allGames);

  // Filter games when any filter changes
  useEffect(() => {
    let result = [...allGames];

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (game) =>
          game.title.toLowerCase().includes(query) ||
          game.description.toLowerCase().includes(query) ||
          game.skills.some((skill) => skill.toLowerCase().includes(query))
      );
    }

    // Category filter
    if (selectedCategory !== 'all') {
      result = result.filter((game) => game.category === selectedCategory);
    }

    // Age filter
    if (selectedAge !== 'all') {
      result = result.filter((game) => game.age === selectedAge);
    }

    // Difficulty filter
    if (selectedDifficulty !== 'all') {
      result = result.filter((game) => game.difficulty === selectedDifficulty);
    }

    // Sort
    switch (sortBy) {
      case 'popular':
        result.sort((a, b) => parseFloat(b.plays) - parseFloat(a.plays));
        break;
      case 'rating':
        result.sort((a, b) => b.rating - a.rating);
        break;
      case 'newest':
        result.sort((a, b) => (b.isNew ? 1 : 0) - (a.isNew ? 1 : 0));
        break;
      case 'alphabetical':
        result.sort((a, b) => a.title.localeCompare(b.title));
        break;
    }

    setFilteredGames(result);
  }, [searchQuery, selectedCategory, selectedAge, selectedDifficulty, sortBy]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-navy-50 via-white to-teal-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-navy-800 to-navy-900 text-white py-8">
        <div className="container mx-auto px-6">
          <Link to="/" className="inline-flex items-center text-teal-300 hover:text-teal-200 mb-4 transition-colors">
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Home
          </Link>
          <div className="flex items-center gap-4 mb-4">
            {/* WHITE LOGO for dark background */}
            <img 
              src="https://storage.googleapis.com/msgsndr/aAuxGGoSZCGAlUv23ouM/media/696acd3a65d73dde431017e2.png" 
              alt="SpeechFormation" 
              className="h-12 w-auto"
            />
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-bold mb-2">Games Library</h1>
          <p className="text-navy-200 font-body text-lg">
            Discover voice-controlled games that make speech practice an adventure
          </p>
        </div>
      </header>

      <div className="container mx-auto px-6 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar Filters */}
          <aside className="lg:w-72 flex-shrink-0">
            <div className="bg-white rounded-3xl p-6 shadow-card sticky top-8">
              {/* Search */}
              <div className="mb-6">
                <label className="block font-heading font-semibold text-navy-800 mb-2">Search Games</label>
                <div className="relative">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search by name or skill..."
                    className="w-full pl-10 pr-4 py-3 border-2 border-navy-100 rounded-xl focus:border-teal-500 focus:outline-none transition-colors font-body"
                  />
                  <svg className="w-5 h-5 text-navy-400 absolute left-3 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
              </div>

              {/* Categories */}
              <div className="mb-6">
                <label className="block font-heading font-semibold text-navy-800 mb-3">Categories</label>
                <div className="space-y-2">
                  {categories.map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => setSelectedCategory(cat.id)}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                        selectedCategory === cat.id
                          ? 'bg-teal-500 text-white shadow-playful'
                          : 'bg-navy-50 text-navy-700 hover:bg-navy-100'
                      }`}
                    >
                      {cat.icon === 'logo-white' ? (
                        <img 
                          src={selectedCategory === cat.id 
                            ? "https://storage.googleapis.com/msgsndr/aAuxGGoSZCGAlUv23ouM/media/696e8f2d15885ed298c711bd.png"
                            : "https://storage.googleapis.com/msgsndr/aAuxGGoSZCGAlUv23ouM/media/696e8f2ddf5fa55637865a98.png"
                          } 
                          alt="" 
                          className="h-5 w-auto" 
                        />
                      ) : (
                        <span className="text-xl">{cat.icon}</span>
                      )}
                      <span className="font-medium">{cat.name}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Age Group */}
              <div className="mb-6">
                <label className="block font-heading font-semibold text-navy-800 mb-3">Age Group</label>
                <div className="flex flex-wrap gap-2">
                  {ageGroups.map((age) => (
                    <button
                      key={age.id}
                      onClick={() => setSelectedAge(age.id)}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                        selectedAge === age.id
                          ? 'bg-coral-500 text-white'
                          : 'bg-navy-50 text-navy-600 hover:bg-navy-100'
                      }`}
                    >
                      {age.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Difficulty */}
              <div className="mb-6">
                <label className="block font-heading font-semibold text-navy-800 mb-3">Difficulty</label>
                <div className="flex flex-wrap gap-2">
                  {difficulties.map((diff) => (
                    <button
                      key={diff.id}
                      onClick={() => setSelectedDifficulty(diff.id)}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                        selectedDifficulty === diff.id
                          ? 'bg-navy-800 text-white'
                          : diff.color
                      }`}
                    >
                      {diff.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Clear Filters */}
              <button
                onClick={() => {
                  setSearchQuery('');
                  setSelectedCategory('all');
                  setSelectedAge('all');
                  setSelectedDifficulty('all');
                }}
                className="w-full py-3 border-2 border-navy-200 text-navy-600 font-heading font-semibold rounded-xl hover:bg-navy-50 transition-colors"
              >
                Clear All Filters
              </button>
            </div>
          </aside>

          {/* Main Content */}
          <main className="flex-1">
            {/* Sort Bar */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
              <p className="text-navy-600 font-body">
                Showing <span className="font-semibold text-navy-800">{filteredGames.length}</span> games
              </p>
              <div className="flex items-center gap-3">
                <label className="text-navy-600 font-body">Sort by:</label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="px-4 py-2 border-2 border-navy-100 rounded-xl focus:border-teal-500 focus:outline-none font-body bg-white"
                >
                  <option value="popular">Most Popular</option>
                  <option value="rating">Highest Rated</option>
                  <option value="newest">Newest First</option>
                  <option value="alphabetical">A-Z</option>
                </select>
              </div>
            </div>

            {/* Games Grid */}
            {filteredGames.length > 0 ? (
              <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-6">
                {filteredGames.map((game) => (
                  <div
                    key={game.id}
                    className="group bg-white rounded-3xl overflow-hidden shadow-card hover:shadow-card-hover transition-all duration-300 hover:-translate-y-2"
                  >
                    {/* Game Visual */}
                    <div className={`h-40 bg-gradient-to-br ${game.color} flex items-center justify-center relative overflow-hidden`}>
                      <span className="text-6xl group-hover:scale-110 transition-transform duration-300">{game.icon}</span>
                      
                      {/* Badges */}
                      <div className="absolute top-3 left-3 flex gap-2">
                        {game.isNew && (
                          <span className="px-3 py-1 bg-white/90 text-coral-600 text-xs font-bold rounded-full">
                            NEW
                          </span>
                        )}
                        {game.isFeatured && (
                          <span className="px-3 py-1 bg-sunshine-400 text-navy-800 text-xs font-bold rounded-full">
                            ⭐ FEATURED
                          </span>
                        )}
                      </div>

                      {/* Play overlay */}
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all">
                        <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-lg transform scale-75 group-hover:scale-100 transition-transform">
                          <svg className="w-8 h-8 text-navy-800 ml-1" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                          </svg>
                        </div>
                      </div>
                    </div>

                    {/* Game Info */}
                    <div className="p-5">
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="font-heading font-bold text-lg text-navy-800">{game.title}</h3>
                        <div className="flex items-center gap-1 text-sunshine-500">
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
                          <span className="text-sm font-semibold text-navy-700">{game.rating}</span>
                        </div>
                      </div>

                      <p className="text-navy-600 font-body text-sm mb-4 line-clamp-2">{game.description}</p>

                      {/* Skills */}
                      <div className="flex flex-wrap gap-2 mb-4">
                        {game.skills.map((skill, i) => (
                          <span key={i} className="px-2 py-1 bg-teal-50 text-teal-700 text-xs rounded-full font-medium">
                            {skill}
                          </span>
                        ))}
                      </div>

                      {/* Meta */}
                      <div className="flex items-center justify-between text-sm text-navy-500">
                        <span className="flex items-center gap-1">
                          <img src="https://storage.googleapis.com/msgsndr/aAuxGGoSZCGAlUv23ouM/media/696e8f2ddf5fa55637865a98.png" alt="" className="h-4 w-auto" />
                          {game.plays} plays
                        </span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          game.difficulty === 'beginner' ? 'bg-mint-100 text-mint-700' :
                          game.difficulty === 'intermediate' ? 'bg-sunshine-100 text-sunshine-700' :
                          'bg-coral-100 text-coral-700'
                        }`}>
                          {game.difficulty.charAt(0).toUpperCase() + game.difficulty.slice(1)}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="text-6xl mb-4">🔍</div>
                <h3 className="font-heading font-bold text-xl text-navy-800 mb-2">No games found</h3>
                <p className="text-navy-600 font-body">Try adjusting your filters or search terms</p>
              </div>
            )}
          </main>
        </div>
      </div>

      {/* CTA Banner */}
      <section className="bg-gradient-to-r from-coral-500 to-coral-600 py-12">
        <div className="container mx-auto px-6 text-center">
          <h2 className="font-display text-2xl md:text-3xl font-bold text-white mb-4">
            Ready to Start Playing?
          </h2>
          <p className="text-coral-100 font-body text-lg mb-6 max-w-xl mx-auto">
            Create a free account and unlock access to our starter games. Upgrade anytime for the full experience.
          </p>
          <Link
            to="/register"
            className="inline-flex items-center px-8 py-4 bg-white text-coral-600 font-heading font-bold rounded-2xl transition-all hover:scale-105 shadow-lg"
          >
            Start Free Trial
            <svg className="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </Link>
        </div>
      </section>
    </div>
  );
}
